import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Bell, CheckCheck } from 'lucide-react';
import { formatDateTime } from '@/lib/format';
import { notificationLabel } from '@/lib/labels';
import type { NotificationDto } from '@/lib/types-tracking';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useMarkRead, useNotifications, useUnreadCount } from './api';

export function NotificationItem({ n, onOpen }: { n: NotificationDto; onOpen: (n: NotificationDto) => void }) {
  return (
    <button type="button" onClick={() => onOpen(n)} className={cn('hover:bg-muted/60 w-full rounded-md p-2 text-left', !n.readAt && 'bg-muted/40')}>
      <div className="flex items-start gap-2">
        <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', n.readAt ? 'bg-transparent' : 'bg-primary')} aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">
            {!n.readAt && <span className="sr-only">Não lida: </span>}
            {n.title}
          </p>
          <p className="text-muted-foreground line-clamp-2 text-xs whitespace-pre-line">{n.body}</p>
          <p className="text-muted-foreground mt-0.5 text-[11px]">
            {notificationLabel(n.type)} · {formatDateTime(n.createdAt)}
          </p>
        </div>
      </div>
    </button>
  );
}

/** Sino com contador de não lidas (60 s) e as notificações mais recentes. */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const count = useUnreadCount();
  const latest = useNotifications(1, false, 8, open);
  const mark = useMarkRead();
  const navigate = useNavigate();
  const unread = count.data?.count ?? 0;
  const openItem = (n: NotificationDto) => {
    if (!n.readAt) mark.mutate(n.id);
    setOpen(false);
    if (n.link) navigate(n.link);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label={unread ? `Notificações: ${unread} não lida(s)` : 'Notificações'}>
          <Bell />
          {unread > 0 && (
            <span className="bg-destructive absolute -top-0.5 -right-0.5 min-w-4 rounded-full px-1 text-center text-[10px] leading-4 font-semibold text-white" aria-hidden data-testid="unread-badge">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-2 sm:w-96">
        <div className="flex items-center justify-between px-1 pb-2">
          <p className="text-sm font-semibold">Notificações</p>
          <Button variant="ghost" size="sm" disabled={!unread || mark.isPending} onClick={() => mark.mutate('all')}>
            <CheckCheck /> Marcar todas como lidas
          </Button>
        </div>
        <div className="max-h-96 space-y-1 overflow-y-auto">
          {latest.isPending ? (
            <p className="text-muted-foreground p-2 text-sm">Carregando…</p>
          ) : latest.isError ? (
            <p className="text-destructive p-2 text-sm">Não foi possível carregar.</p>
          ) : latest.data.items.length === 0 ? (
            <p className="text-muted-foreground p-2 text-sm">Nenhuma notificação.</p>
          ) : (
            latest.data.items.map((n) => <NotificationItem key={n.id} n={n} onOpen={openItem} />)
          )}
        </div>
        <div className="border-t pt-2 text-center">
          <Link to="/notificacoes" onClick={() => setOpen(false)} className="text-sm underline-offset-4 hover:underline">
            Ver todas
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
